import React, { memo, type ReactNode } from "react";
import { View, ScrollView, Platform } from "react-native";
import { KeyboardAwareScrollView } from "@/components/ui/KeyboardAvoidingView";
import { AppText } from "@/components/ui/AppText";
import { useI18n } from "@/context/I18nContext";
import { useIsWideScreen } from "@/hooks/useIsWideScreen";
import { Button } from "@/components/ui/Button";
import { InfoCard } from "@/components/ui/InfoCard";
import { Feather, MaterialCommunityIcons } from "@expo/vector-icons";
import { Icon, IconProps } from "@/components/ui/Icon";
import { ProgressBar } from "@/components/ui/ProgressBar";
import { StepWizard } from "@/components/ui/StepWizard";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { useRouter } from "expo-router";
import { cn } from "@/lib/utils";
import { PageFooter } from "@/components/ui/PageFooter";
import { useSafeAreaInsets } from "react-native-safe-area-context";

interface StepShellProps {
  step: number;
  totalSteps?: number;
  children: ReactNode;
  onNext?: () => void;
  onBack?: () => void;
  nextLoading?: boolean;
  isLastStep?: boolean;
  nextLabel?: TranslationKeys;
  iconName: IconProps["name"];
  title: TranslationKeys;
  subtitle: TranslationKeys;
}

const TOTAL_STEPS = 5;
const FLEX_STYLE = { flex: 1 } as const;

const STEP_SHORT_LABELS: TranslationKeys[] = [
  "preVetting.step1.shortLabel",
  "preVetting.step2.shortLabel",
  "preVetting.step3.shortLabel",
  "preVetting.step4.shortLabel",
  "preVetting.step5.shortLabel",
];

// ─── StepShell ───────────────────────────────────────────────────────────────

export const StepShell = memo(function StepShell({
  step,
  totalSteps = TOTAL_STEPS,
  children,
  onNext,
  onBack,
  nextLoading,
  isLastStep,
  nextLabel,
  title,
  subtitle,
  iconName,
}: StepShellProps) {
  const { t, isRTL } = useI18n();
  const { bottom } = useSafeAreaInsets();
  const isWide = useIsWideScreen();
  const router = useRouter();

  // ── Wide-screen web layout (≥768px) ────────────────────────────────────────
  if (isWide) {
    return (
      <>
        {/* Go back link */}
        <View
          className={cn(
            "px-page-x w-full tablet:border-b tablet:border-stroke-50 bg-white py-2",
            Platform.select({ native: "hidden" })
          )}
        >
          <View className="w-full self-center items-start ">
            <Button
              className="pl-0 py-2"
              label="preVetting.goBack"
              onPress={router.back}
              variant="text"
              icon={
                <Icon
                  as={Feather}
                  name={isRTL ? "arrow-right" : "arrow-left"}
                  size={16}
                  className="text-primary"
                />
              }
            />
          </View>
        </View>
        <ScrollView
          className="flex-1 bg-stroke-15"
          contentContainerStyle={{ flexGrow: 1 }}
          showsVerticalScrollIndicator={false}
        >
          <View className="w-full px-page-x lg:px-0">
            <View className={`self-center w-full py-page-y pb-2 mb-6 flex lg:hidden`}>
              <AppText className="text-xs text-dark-text-15 mb-2 uppercase tracking-widest">
                {t("preVetting.stepOf", { current: step, total: totalSteps })}
              </AppText>
              <ProgressBar step={step} totalSteps={totalSteps} />
            </View>

            <View className="mb-16 bg-white w-full self-center px-page-x hidden lg:flex">
              {/* Page title */}
              <View className="items-center py-8">
                <AppText weight="bold" className="text-3xl text-dark-text-50 mb-1">
                  {t("preVetting.webTitle")}
                </AppText>
                <AppText className="text-sm text-supporting-text" weight="medium">
                  {t("preVetting.webSubtitle")}
                </AppText>
              </View>

              {/* Horizontal step wizard */}
              <StepWizard step={step} labels={STEP_SHORT_LABELS.slice(0, totalSteps)} />
            </View>

            {/* White content card */}
            <View className="bg-white border border-stroke-50 rounded-xl w-full self-center mb-10 overflow-hidden max-w-7xl">
              {/* ─── Step header ───────────────────────────────────────── */}
              <View className="px-5 lg:px-10 py-4 lg:py-8 bg-stroke-30">
                <View className="flex-row items-start gap-3">
                  <Icon
                    as={MaterialCommunityIcons}
                    name={iconName}
                    size={32}
                    className="text-blue-50"
                  />
                  <View className="flex-1 items-start">
                    <AppText className="text-xl text-dark-text-50" weight="bold">
                      {t(title)}
                    </AppText>
                    <AppText weight="medium" className="text-sm text-supporting-text mt-1">
                      {t(subtitle)}
                    </AppText>
                  </View>
                </View>
              </View>

              <View className="px-5 lg:px-10 pt-6 pb-4">{children}</View>
              {/* Action buttons inside card bottom */}
              <View className="px-5 lg:px-10 flex-row justify-between items-center py-4 gap-3 bg-stroke-30">
                <Button
                  className="bg-white border-stroke-50"
                  label="common.back"
                  onPress={() => onBack?.()}
                  variant="secondary"
                  disabled={!onBack}
                  icon={
                    <Icon
                      as={Feather}
                      name={isRTL ? "arrow-right" : "arrow-left"}
                      size={16}
                      className="text-primary"
                    />
                  }
                />
                <View className="flex-row  gap-3 items-center">
                  <View>
                    {onNext && (
                      <Button
                        className="max-w-sm"
                        label={nextLabel ?? (isLastStep ? "common.submit" : "common.next")}
                        onPress={onNext}
                        loading={nextLoading}
                        variant="primary"
                        trailingIcon={
                          !isLastStep ? (
                            <Icon
                              as={Feather}
                              name={isRTL ? "arrow-left" : "arrow-right"}
                              size={16}
                              color="white"
                            />
                          ) : undefined
                        }
                      />
                    )}
                  </View>
                </View>
              </View>
            </View>

            {/* 3-column info cards */}
            <View className="flex-row gap-3 tablet:max-w-7xl w-full self-center mb-20">
              <View className="flex-1">
                <InfoCard
                  icon={
                    <Icon
                      as={MaterialCommunityIcons}
                      name="shield-star"
                      size={24}
                      className="text-blue-50"
                    />
                  }
                  variant="secure"
                  title="infoCard.secure.title"
                  description="infoCard.secure.description"
                />
              </View>
              <View className="flex-1">
                <InfoCard
                  icon={
                    <Icon
                      as={MaterialCommunityIcons}
                      name="information"
                      size={24}
                      className="text-orange"
                    />
                  }
                  variant="assist"
                  title="infoCard.assist.title"
                  description="infoCard.assist.description"
                />
              </View>
              <View className="flex-1">
                <InfoCard
                  icon={
                    <Icon
                      as={MaterialCommunityIcons}
                      name="check-decagram"
                      size={24}
                      className="text-success"
                    />
                  }
                  variant="approval"
                  title="infoCard.approval.title"
                  description="infoCard.approval.description"
                />
              </View>
            </View>
          </View>

          <PageFooter />
        </ScrollView>
      </>
    );
  }

  // ── Native / mobile layout ─────────────────────────────────────────────────

  return (
    <>
      <KeyboardAwareScrollView
        className="flex-1 bg-stroke-15 px-page-x py-page-t tablet:py-page-t"
        contentContainerStyle={{ flexGrow: 1, paddingBottom: bottom + 40 }}
        showsVerticalScrollIndicator={false}
      >
        <View className="tablet:max-w-3xl w-full mx-auto">
          <View className={`self-center w-full pb-2 mb-6`}>
            <AppText className="text-xs text-dark-text-15 mb-2 uppercase tracking-widest">
              {t("preVetting.stepOf", { current: step, total: totalSteps })}
            </AppText>
            <ProgressBar step={step} totalSteps={totalSteps} />
          </View>
          {/* ─── Step header ───────────────────────────────────────── */}
          <View className="flex-row items-start gap-3 mb-6">
            <Icon as={MaterialCommunityIcons} name={iconName} size={32} className="text-blue-50" />
            <View className="flex-1">
              <AppText className="text-xl text-dark-text-50" weight="bold">
                {t(title)}
              </AppText>
              <AppText weight="medium" className="text-sm text-supporting-text mt-1">
                {t(subtitle)}
              </AppText>
            </View>
          </View>
          <View className="bg-white border border-stroke-50 px-4 py-6 rounded-md overflow-hidden mb-10">
            <View className={`self-center w-full pb-4`}>{children}</View>

            <View className={`self-center w-full gap-3 ${isRTL ? "items-end" : "items-start"}`}>
              <View className="lg:flex-row gap-3 w-full">
                <Button
                  label="common.back"
                  onPress={() => onBack?.()}
                  variant="secondary"
                  disabled={!onBack}
                  icon={
                    <Icon
                      as={Feather}
                      name={isRTL ? "arrow-right" : "arrow-left"}
                      size={16}
                      className="text-primary"
                    />
                  }
                />
                {onNext && (
                  <Button
                    label={nextLabel ?? (isLastStep ? "common.submit" : "common.next")}
                    onPress={onNext}
                    loading={nextLoading}
                    variant="primary"
                    style={FLEX_STYLE}
                  />
                )}
              </View>
            </View>
          </View>
          <View className={`self-center tablet:max-w-3xl w-full pb-4 gap-3`}>
            <InfoCard
              variant="secure"
              title="infoCard.secure.title"
              description="infoCard.secure.description"
            />
            <InfoCard
              variant="assist"
              title="infoCard.assist.title"
              description="infoCard.assist.description"
            />
            <InfoCard
              variant="approval"
              title="infoCard.approval.title"
              description="infoCard.approval.description"
            />
          </View>
        </View>
      </KeyboardAwareScrollView>
    </>
  );
});
